import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

interface FactRowProps {
  /**
   * Usually a word. A node is allowed so a row that heads a section can carry the real heading
   * element — `EventThread`'s Discussion row is an `<h2>` — without a second copy of this layout
   * existing to hold one. The wrapper supplies the type, so a heading passed here wants
   * `font: inherit`.
   */
  label: ReactNode;
  /** The primary value. Wraps rather than shrinking — never reduced to fit (spec §8). */
  children: ReactNode;
  /** Second line under the value, e.g. a street address beneath a place name. */
  secondary?: ReactNode;
  icon?: LucideIcon;
  /** Digits that tick (times, dates, counts) get tabular figures. */
  tabular?: boolean;
  /**
   * A third column, right-aligned, in the same place {@link DocketRow} puts its chevron — so a
   * page mixing the two keeps one unbroken column of affordances down the right edge. Keep it
   * to one control and let it `nowrap`; the value beside it is the thing allowed to wrap.
   */
  trailing?: ReactNode;
  /**
   * Ink when this row heads the content beneath it, muted when it is one fact among many. Same
   * distinction `DocketRow` draws between an open row and a closed one, and drawn the same way —
   * so the Discussion row reading as a heading costs no new colour, weight or size.
   */
  labelTone?: 'muted' | 'ink';
}

/**
 * The label/value row from §5A. Per §10, this same layout is reused for *any*
 * label/value pair anywhere in the product rather than inventing a variant.
 *
 * 100px label column, 44px min row height, 1px subtle bottom border.
 */
export function FactRow({
  label,
  children,
  secondary,
  icon: Icon,
  tabular = true,
  trailing,
  labelTone = 'muted',
}: FactRowProps) {
  return (
    <div
      className="grid items-start"
      style={{
        gridTemplateColumns: trailing ? '100px 1fr auto' : '100px 1fr',
        gap: 'var(--space-3)',
        minHeight: '44px',
        padding: 'var(--space-3) 0',
        borderBottom: '1px solid var(--color-border-subtle)',
      }}
    >
      <div
        className="t-label flex items-center gap-2"
        style={{
          color: labelTone === 'ink' ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
          paddingTop: '2px',
        }}
      >
        {/* Never squeezed. A flex item with no basis, an SVG will give up its own width before
            the text beside it wraps -- so the longest label on a page silently deforms its icon
            rather than overflowing, and the row that looks wrong is the one with the small icon
            rather than the one with the long word. Found at 6px of a 14px icon on Discussion. */}
        {Icon && <Icon size={14} aria-hidden="true" className="shrink-0" />}
        {label}
      </div>

      <div>
        <div
          className={`t-fact ${tabular ? 'tabular' : ''}`}
          style={{ color: 'var(--color-text-primary)' }}
        >
          {children}
        </div>
        {secondary && (
          <div className="t-caption" style={{ color: 'var(--color-text-secondary)' }}>
            {secondary}
          </div>
        )}
      </div>

      {trailing && (
        <div className="flex items-center" style={{ paddingTop: '2px' }}>
          {trailing}
        </div>
      )}
    </div>
  );
}
